import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useOnboardingBlocked } from "@/pages/onboarding/OnboardingStep";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Eye,
  EyeOff,
  FileText,
  Loader2,
  Trash2,
  Upload,
} from "lucide-react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { OnboardingBrandColumn } from "@/components/onboarding/OnboardingBrandColumn";
import { OnboardingBrandMark } from "@/components/onboarding/OnboardingBrandMark";
import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import { OnboardingStepTag } from "@/components/onboarding/OnboardingStepTag";
import {
  getDgiConfigTrail,
  getDgiTrailCurrentStep,
} from "@/components/onboarding/OnboardingTrail";
import { useCertificateUpload } from "@/hooks/useCertificateUpload";
import { useAuth } from "@/contexts/AuthContext";
import { tenantKeys } from "@/lib/queryKeys";
import { cn } from "@/lib/utils";
import {
  DGI_CERTIFICATE_FILE_ACCEPT,
  getTenantCurrent,
} from "@/services/authService";
import { formatDisplayDate } from "@/lib/dateFormat";
import { updateTenant } from "@/services/authService";
import { NotarialCertificateSection } from "@/pages/onboarding/NotarialCertificateSection";
import { ProviderRequestTemplateDialog } from "@/pages/onboarding/ProviderRequestTemplateDialog";

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

interface CertificateUploadInlineProps {
  tenantRut: string | null;
  onSuccess: () => void | Promise<void>;
  onCancel: () => void;
  /** Optional skip CTA — rendered inline beside the primary submit. */
  onSkip?: () => void;
  skipLoading?: boolean;
  /**
   * Switches this tenant to the Fiscalo/Abitab request path. Offered when the
   * backend rejects the upload as expired — without it that rejection is a dead
   * end, which is the whole reason the expiry check was added.
   */
  onRequestNewCertificate?: () => void;
}

/**
 * Editorial-styled certificate upload — drop zone IS the input. After the
 * user picks/drops a file we show a file card + passphrase field + accent
 * submit. Self-contained so the surrounding shell can stay narrative.
 */
function CertificateUploadInline({
  tenantRut,
  onSuccess,
  onCancel,
  onSkip,
  skipLoading = false,
  onRequestNewCertificate,
}: CertificateUploadInlineProps): JSX.Element {
  const { t } = useTranslation();
  const reportBlocked = useOnboardingBlocked();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [passVisible, setPassVisible] = useState(false);
  const {
    file,
    setFile,
    passphrase,
    setPassphrase,
    uploading,
    submit,
    reset,
    canSubmit,
    expiredOn,
  } = useCertificateUpload({ tenantRut, onSuccess });

  const triggerBrowse = (): void => fileInputRef.current?.click();
  const handleFiles = (files: FileList | null): void => {
    if (!files || files.length === 0) return;
    const picked = files[0];
    if (!picked) return;
    // Guard against non-PKCS12 drops — the file picker filters by .pfx
    // but drag-and-drop bypasses it. Reject here with a specific message
    // instead of letting the upload fail with the generic
    // "P12 inválido o contraseña incorrecta" toast.
    const lower = picked.name.toLowerCase();
    if (!lower.endsWith(".pfx") && !lower.endsWith(".p12")) {
      reportBlocked("cert_file_type_invalid");
      toast.error(
        t(
          "onboarding.certStep.errorFileType",
          "El archivo no parece ser un certificado .pfx/.p12. Seleccioná el archivo que te entregó DGI o tu proveedor.",
        ),
      );
      return;
    }
    setFile(picked);
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={DGI_CERTIFICATE_FILE_ACCEPT}
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />

      {file ? (
        <div className="fx-onb-file-card">
          <span className="tile" aria-hidden>
            <FileText />
          </span>
          <span className="body">
            <span className="name">
              {file.name}
              <span className="ext">
                {file.name.split(".").pop()?.toUpperCase() ?? "PFX"}
              </span>
            </span>
            <span className="meta">{formatBytes(file.size)}</span>
          </span>
          <span className="actions">
            <button
              type="button"
              onClick={triggerBrowse}
              disabled={uploading}
              aria-label={t("onboarding.certStep.replaceFile", "Reemplazar")}
            >
              <Upload />
            </button>
            <button
              type="button"
              onClick={() => reset()}
              disabled={uploading}
              aria-label={t("onboarding.certStep.removeFile", "Quitar")}
            >
              <Trash2 />
            </button>
          </span>
        </div>
      ) : (
        <button
          type="button"
          className={cn("fx-onb-drop-zone", dragging && "dragging")}
          onClick={triggerBrowse}
          onDragOver={(e) => {
            e.preventDefault();
            if (!dragging) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            handleFiles(e.dataTransfer.files);
          }}
          style={{ width: "100%" }}
        >
          <span className="ico-tile" aria-hidden>
            <Upload />
          </span>
          <p className="title">
            {t(
              "onboarding.certStep.dropTitleLead",
              "Arrastrá tu certificado",
            )}{" "}
            <span className="ital">
              {t("onboarding.certStep.dropTitleAccent", "acá.")}
            </span>
          </p>
          <p className="sub">
            {t(
              "onboarding.certStep.dropSub",
              "Aceptamos .pfx — máximo 1 MB · o tocá para seleccionar",
            )}
          </p>
          <span className="meta">
            <span className="pill">PKCS#12</span>
            <span>
              {t(
                "onboarding.certStep.dropTls",
                "Cifrado en tránsito · TLS 1.3",
              )}
            </span>
          </span>
        </button>
      )}

      {file ? (
        <div className="fx-onb-form" style={{ marginTop: 16 }}>
          <div className="fx-onb-field">
            <label htmlFor="cert-passphrase">
              {t(
                "onboarding.certStep.passphraseLabel",
                "Contraseña del certificado",
              )}
            </label>
            <div style={{ position: "relative" }}>
              <input
                id="cert-passphrase"
                type={passVisible ? "text" : "password"}
                value={passphrase}
                onChange={(e) => setPassphrase(e.target.value)}
                disabled={uploading}
                placeholder={t(
                  "onboarding.certStep.passphrasePlaceholder",
                  "La contraseña que protege el .pfx",
                )}
                autoComplete="off"
                style={{ paddingRight: 44 }}
              />
              <button
                type="button"
                onClick={() => setPassVisible((v) => !v)}
                aria-label={
                  passVisible
                    ? t("onboarding.certStep.hidePassphrase", "Ocultar")
                    : t("onboarding.certStep.showPassphrase", "Mostrar")
                }
                style={{
                  position: "absolute",
                  right: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "transparent",
                  border: "none",
                  color: "var(--fx-ink-500)",
                  cursor: "pointer",
                  padding: 4,
                  display: "grid",
                  placeItems: "center",
                }}
              >
                {passVisible ? (
                  <EyeOff style={{ width: 16, height: 16 }} />
                ) : (
                  <Eye style={{ width: 16, height: 16 }} />
                )}
              </button>
            </div>
            <span className="hint">
              {t(
                "onboarding.certStep.passphraseHint",
                "La contraseña nunca se guarda — la usamos solo para descifrar tu llave una vez.",
              )}
            </span>
          </div>
        </div>
      ) : null}

      {expiredOn && onRequestNewCertificate ? (
        <div
          role="alert"
          style={{
            marginTop: 16,
            padding: "12px 14px",
            borderRadius: "var(--fx-r-md)",
            background: "rgba(176,58,46,0.06)",
            border: "1px solid rgba(176,58,46,0.22)",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <span style={{ fontWeight: 600 }}>
            {t("onboarding.certStep.expiredTitle", {
              date: formatDisplayDate(expiredOn),
              defaultValue: "Ese certificado venció el {{date}}.",
            })}
          </span>
          <span style={{ fontSize: 13, lineHeight: 1.45 }}>
            {t(
              "onboarding.certStep.expiredBody",
              "Un certificado vencido no puede firmar comprobantes: DGI los rechaza todos. Necesitás uno nuevo.",
            )}
          </span>
          <button
            type="button"
            className="fx-onb-btn fx-onb-btn-accent"
            style={{ alignSelf: "flex-start", marginTop: 4 }}
            onClick={onRequestNewCertificate}
          >
            {t(
              "onboarding.certStep.expiredRequestCta",
              "Pedir uno nuevo a Abitab",
            )}
          </button>
        </div>
      ) : null}

      <div className="fx-onb-actions" style={{ marginTop: 16 }}>
        <div className="side">
          <button
            type="button"
            className="fx-onb-btn fx-onb-btn-secondary"
            onClick={onCancel}
            disabled={uploading}
          >
            <ArrowLeft aria-hidden />
            {t("onboarding.back", "Volver")}
          </button>
        </div>
        <div className="side end" style={{ gap: 8 }}>
          {onSkip ? (
            <button
              type="button"
              className="fx-onb-btn fx-onb-btn-ghost"
              onClick={onSkip}
              disabled={uploading || skipLoading}
            >
              {skipLoading ? (
                <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} />
              ) : null}
              {t("onboarding.certStep.skipButton", "Omitir")}
            </button>
          ) : null}
          <button
            type="button"
            className="fx-onb-btn fx-onb-btn-accent"
            disabled={!canSubmit || uploading}
            onClick={() => void submit()}
          >
            {uploading ? (
              <Loader2 className="animate-spin" />
            ) : (
              <>
                {t(
                  "onboarding.certStep.submitCta",
                  "Subir y validar certificado",
                )}
                <ArrowRight aria-hidden />
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
}

export const NOTARIAL_DOCUMENT_NAME = "Notarial document for certificate";

const CERTIFICATE_PATH_KEY_PREFIX = "onboarding.certificatePath.";
const FISCALO_SUBSTEP_KEY_PREFIX = "onboarding.fiscaloCertSubstep.";

export function getCertificatePathStorageKey(tenantId: string): string {
  return `${CERTIFICATE_PATH_KEY_PREFIX}${tenantId}`;
}

export function getFiscaloSubstepStorageKey(tenantId: string): string {
  return `${FISCALO_SUBSTEP_KEY_PREFIX}${tenantId}`;
}

export type CertificatePath = "manual" | "fiscalo" | null;

export interface CertificateStepContentProps {
  hasDgiCredentials: boolean;
  tenantId: string;
  tenantRut: string | null;
  certificatePath: CertificatePath;
  setCertificatePath: (path: "manual" | "fiscalo" | null) => void;
  fiscaloCertSubstep: 0 | 1;
  setFiscaloCertSubstep: (step: 0 | 1) => void;
  onSuccess: () => void | Promise<void>;
  onBackToPathChoice?: () => void;
  alreadyIssuerHint?: boolean;
  /** True when an "Importar CFE anteriores" step follows the certificate
   *  step — drives the 4th trail entry + "paso 03 de 04" indicator. */
  hasCfeImportNext?: boolean;
  /** Drives whether the trail surfaces a "Certificado" 4th step
   *  (scratch / regularization paths) and which row is current. */
  situationChoice?: "scratch" | "issuer_need_regularization" | "already_issuer" | null;
  /** Continue handler for the "already uploaded" success view — advances
   *  past the cert step (the parent decides the next step). */
  onContinueFromDone?: () => void;
  /** Skip handler — renders an inline "Omitir" banner when provided.
   *  Marks dgiCertificateSkippedAt server-side so the user can advance
   *  without a cert (they have to upload it later in Settings to issue). */
  onSkip?: () => void;
  /** Spinner state for the Omitir button. */
  skipLoading?: boolean;
}

interface CertificateBrandColumnProps {
  hasCfeImportNext: boolean;
  alreadyIssuerHint: boolean;
  onOpenTemplate: () => void;
  situationChoice: "scratch" | "issuer_need_regularization" | "already_issuer" | null;
}

function CertificateBrandColumn({
  hasCfeImportNext,
  alreadyIssuerHint,
  onOpenTemplate,
  situationChoice,
}: CertificateBrandColumnProps): JSX.Element {
  const { t } = useTranslation();
  const trailSteps = getDgiConfigTrail({
    situationChoice,
    isMigratingCfes: hasCfeImportNext,
  });
  // viewStep 6 is the cert confirm/progress screen. For scratch /
  // regularization that row is "Certificado" (index 3). For
  // already_issuer it's the unified "Configuración" row (index 3).
  // Helper centralizes the mapping so we don't drift.
  const currentStep = getDgiTrailCurrentStep(6, situationChoice);
  return (
    <OnboardingBrandColumn
      eyebrow={t(
        "onboarding.certStep.brandEyebrow",
        "Tu certificado digital",
      )}
      quote={
        <>
          {t(
            "onboarding.certStep.brandQuoteLead",
            "La llave criptográfica que nos permite",
          )}{" "}
          <span className="accent">
            {t(
              "onboarding.certStep.brandQuoteAccent",
              "firmar tus CFE en nombre de tu empresa.",
            )}
          </span>
        </>
      }
      currentStep={currentStep}
      trailSteps={trailSteps}
      afterTrail={
        <Collapsible className="fx-onb-value-block fx-onb-collapsible">
          <CollapsibleTrigger className="trigger">
            <span>
              {t("onboarding.certStep.valueHead", "Cómo lo cuidamos")}
            </span>
            <span aria-hidden className="chev">▾</span>
          </CollapsibleTrigger>
          <CollapsibleContent>
          <ul>
            {[
              t(
                "onboarding.certStep.valueItem1",
                "HSM dedicado · clave nunca en disco",
              ),
              t(
                "onboarding.certStep.valueItem2",
                "Cero terceros · acceso solo Fiscalo",
              ),
              t(
                "onboarding.certStep.valueItem3",
                "Aviso 60 días antes del vencimiento",
              ),
              t(
                "onboarding.certStep.valueItem4",
                "Rotás cuando quieras desde tu panel",
              ),
            ].map((item, i) => (
              <li key={i}>
                <span className="ico">
                  <Check aria-hidden />
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
          </CollapsibleContent>
        </Collapsible>
      }
      extra={
        <div className="fx-onb-help-block">
          <span className="title">
            {t("onboarding.certStep.helpTitle", "¿No tenés certificado?")}
          </span>
          {alreadyIssuerHint ? (
            <button
              type="button"
              onClick={onOpenTemplate}
              className="link"
            >
              {t(
                "onboarding.alreadyIssuerCertHintTemplateButton",
                "Ver plantilla de pedido",
              )}{" "}
              →
            </button>
          ) : (
            <a
              href="mailto:certificados@fiscalo.uy"
              className="link"
            >
              {t("onboarding.certStep.helpLink", "Te mostramos cómo →")}
            </a>
          )}
        </div>
      }
    />
  );
}

export function CertificateStepContent({
  hasDgiCredentials,
  tenantId,
  tenantRut,
  certificatePath,
  setCertificatePath,
  fiscaloCertSubstep,
  setFiscaloCertSubstep,
  onSuccess,
  onBackToPathChoice,
  alreadyIssuerHint = false,
  hasCfeImportNext = false,
  situationChoice = null,
  onContinueFromDone,
  onSkip,
  skipLoading = false,
}: CertificateStepContentProps): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [templateOpen, setTemplateOpen] = useState(false);
  const isCertBeforeDgi =
    situationChoice === "scratch" ||
    situationChoice === "issuer_need_regularization";
  // New-cert paths use a 4-row trail, so the step-tag total has to match — same
  // for already_issuer with CFE-import.
  const stepTagTotal = isCertBeforeDgi || hasCfeImportNext ? 4 : 3;
  // "Certificado" row: scratch (DGI-first) is row 4; cert-first regularization
  // and already_issuer are row 3.
  const certStepTagStep = situationChoice === "scratch" ? 4 : 3;
  const brandColumnProps = {
    hasCfeImportNext,
    alreadyIssuerHint,
    onOpenTemplate: () => setTemplateOpen(true),
    situationChoice,
  };
  // Only fetch the tenant when we'll need legalName for the template
  // dialog. Reuses the same query key as TenantContextBar so React Query
  // dedupes the call.
  const { data: currentTenant } = useQuery({
    queryKey: tenantKeys.current(user?.tenantId ?? undefined),
    queryFn: getTenantCurrent,
    enabled: alreadyIssuerHint && Boolean(user?.tenantId),
  });

  const persistPath = (path: "manual" | "fiscalo"): void => {
    setCertificatePath(path);
    // Durable, not tab-scoped. The sessionStorage version of this is what made
    // a routing change unable to take effect for anyone with an open tab: the
    // old value kept winning and nothing server-side could clear it.
    void updateTenant({ onboardingCertPath: path }).catch(() => {
      // ignore — local state already moved the user; a failed write costs a
      // re-answer on the next visit, not a stuck step
    });
  };

  const persistFiscaloSubstep = (step: 0 | 1): void => {
    setFiscaloCertSubstep(step);
    try {
      sessionStorage.setItem(getFiscaloSubstepStorageKey(tenantId), String(step));
    } catch {
      // ignore
    }
  };

  // ── Already uploaded (success state) ─────────────────────────────────
  if (hasDgiCredentials) {
    return (
      <OnboardingShell
        form={
          <>
            <OnboardingBrandMark />
            <OnboardingStepTag
              step={certStepTagStep}
              total={stepTagTotal}
              label={t("onboarding.certStep.tagDone", "Certificado")}
              flow={t("onboarding.certStep.flow", "Certificado digital")}
            />
            <div className="fx-onb-success-hero">
              <span className="ring" aria-hidden>
                <Check />
              </span>
              <h1 className="fx-onb-h1">
                {t("onboarding.step2Done", "Certificado cargado.")}
              </h1>
              <p className="fx-onb-lead">
                {t(
                  "onboarding.certificateStepDone",
                  "Ya podemos firmar tus comprobantes en nombre de tu empresa.",
                )}
              </p>
            </div>
            {onContinueFromDone ? (
              <div className="fx-onb-actions" style={{ marginTop: 12 }}>
                <div className="side" />
                <div className="side end">
                  <button
                    type="button"
                    className="fx-onb-btn fx-onb-btn-accent"
                    onClick={onContinueFromDone}
                  >
                    {t("onboarding.certStep.doneCta", "Continuar")}
                    <ArrowRight aria-hidden />
                  </button>
                </div>
              </div>
            ) : null}
          </>
        }
        brand={<CertificateBrandColumn {...brandColumnProps} />}
      />
    );
  }

  // ── Choice: upload now? ──────────────────────────────────────────────
  if (certificatePath === null) {
    return (
      <OnboardingShell
        form={
          <>
            <OnboardingBrandMark />
            <OnboardingStepTag
              step={certStepTagStep}
              total={stepTagTotal}
              label={t("onboarding.certStep.tagChoice", "Certificado")}
              flow={t("onboarding.certStep.flow", "Certificado digital")}
            />
            <h1 className="fx-onb-h1">
              {t("onboarding.certStep.choiceH1Lead", "Subí tu certificado")}{" "}
              <span className="ital">
                {t("onboarding.certStep.choiceH1Accent", "digital.")}
              </span>
            </h1>
            <p className="fx-onb-lead">
              {t(
                "onboarding.certificateQuestion",
                "Es el archivo .p12 que DGI te entregó cuando te diste de alta como emisor electrónico. Sin esto Fiscalo no puede firmar tus comprobantes.",
              )}
            </p>

            <div className="fx-onb-actions" style={{ marginTop: "auto" }}>
              <div className="side">
                {onBackToPathChoice ? (
                  <button
                    type="button"
                    className="fx-onb-btn fx-onb-btn-secondary"
                    onClick={onBackToPathChoice}
                  >
                    <ArrowLeft aria-hidden />
                    {t("onboarding.back", "Volver")}
                  </button>
                ) : null}
              </div>
              <div className="side end">
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-accent"
                  onClick={() => persistPath("manual")}
                >
                  {t(
                    "onboarding.certificateStepYesUpload",
                    "Subir certificado",
                  )}
                  <ArrowRight aria-hidden />
                </button>
              </div>
            </div>
          </>
        }
        brand={<CertificateBrandColumn {...brandColumnProps} />}
      />
    );
  }

  // ── Manual upload ────────────────────────────────────────────────────
  if (certificatePath === "manual") {
    return (
      <OnboardingShell
        form={
          <>
            <OnboardingBrandMark />
            <OnboardingStepTag
              step={certStepTagStep}
              total={stepTagTotal}
              label={t("onboarding.certStep.tagUpload", "Subir certificado")}
              flow={t("onboarding.certStep.flow", "Certificado digital")}
            />
            <h1 className="fx-onb-h1">
              {t("onboarding.certStep.uploadH1Lead", "Subí tu certificado")}{" "}
              <span className="ital">
                {t("onboarding.certStep.uploadH1Accent", "digital.")}
              </span>
            </h1>
            <p className="fx-onb-lead">
              {alreadyIssuerHint
                ? t(
                    "onboarding.certificateStepManualIntroAlreadyIssuer",
                    "Exportá tu certificado .p12 desde tu proveedor actual y subilo acá.",
                  )
                : t(
                    "onboarding.certificateStepManualIntro",
                    "Es el archivo .p12 que DGI te entregó cuando te diste de alta como emisor electrónico.",
                  )}
            </p>

            <ProviderRequestTemplateDialog
              open={templateOpen}
              onOpenChange={setTemplateOpen}
              tenantLegalName={
                currentTenant?.legalName ?? currentTenant?.name ?? null
              }
              tenantRut={tenantRut}
            />

            <CertificateUploadInline
              tenantRut={tenantRut}
              onSuccess={onSuccess}
              onSkip={onSkip}
              skipLoading={skipLoading}
              onRequestNewCertificate={() => {
                persistPath("fiscalo");
                persistFiscaloSubstep(0);
              }}
              onCancel={() => {
                setCertificatePath(null);
                // Clear the durable path as well, or backing out of the
                // Fiscalo flow would leave the tenant pinned to it on their
                // next visit.
                void updateTenant({ onboardingCertPath: null }).catch(() => {
                  // ignore
                });
                try {
                  sessionStorage.removeItem(
                    getFiscaloSubstepStorageKey(tenantId),
                  );
                } catch {
                  // ignore
                }
              }}
            />
          </>
        }
        brand={<CertificateBrandColumn {...brandColumnProps} />}
      />
    );
  }

  // ── Fiscalo path · disclaimer substep ────────────────────────────────
  if (certificatePath === "fiscalo" && fiscaloCertSubstep === 0) {
    return (
      <OnboardingShell
        form={
          <>
            <OnboardingBrandMark />
            <OnboardingStepTag
              step={certStepTagStep}
              total={stepTagTotal}
              label={t("onboarding.certStep.tagFiscaloDisclaimer", "Antes de seguir")}
              flow={t("onboarding.certStep.flow", "Certificado digital")}
            />
            <h1 className="fx-onb-h1">
              {t("onboarding.certStep.fiscaloH1Lead", "Tu certificado")}{" "}
              <span className="ital">
                {t("onboarding.certStep.fiscaloH1Accent", "vía Fiscalo.")}
              </span>
            </h1>
            <p className="fx-onb-lead">
              {t(
                "onboarding.certificateStepFiscaloDisclaimer",
                "Te ayudamos a sacar tu certificado en ID Digital. Solo necesitamos algunos datos para empezar.",
              )}
            </p>

            <div className="fx-onb-actions" style={{ marginTop: "auto" }}>
              <div className="side">
                {onBackToPathChoice ? (
                  <button
                    type="button"
                    className="fx-onb-btn fx-onb-btn-secondary"
                    onClick={onBackToPathChoice}
                  >
                    <ArrowLeft aria-hidden />
                    {t("onboarding.back", "Volver")}
                  </button>
                ) : null}
              </div>
              <div className="side end">
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-accent"
                  onClick={() => persistFiscaloSubstep(1)}
                >
                  {t("onboarding.certificateStepFiscaloContinue")}
                  <ArrowRight aria-hidden />
                </button>
              </div>
            </div>
          </>
        }
        brand={<CertificateBrandColumn {...brandColumnProps} />}
      />
    );
  }

  // ── Fiscalo path · notarial substep ──────────────────────────────────
  if (certificatePath === "fiscalo" && fiscaloCertSubstep === 1) {
    return (
      <OnboardingShell
        variant="wide"
        form={
          <>
            <OnboardingBrandMark />
            <OnboardingStepTag
              step={certStepTagStep}
              total={stepTagTotal}
              label={t("onboarding.certStep.tagFiscaloNotarial", "Documento notarial")}
              flow={t("onboarding.certStep.flow", "Certificado digital")}
            />
            <h1 className="fx-onb-h1">
              {t("onboarding.certStep.notarialH1Lead", "Subí el")}{" "}
              <span className="ital">
                {t("onboarding.certStep.notarialH1Accent", "documento notarial.")}
              </span>
            </h1>
            <p className="fx-onb-lead">
              {t("onboarding.certificateStepNotarialIntro")}
            </p>
            <NotarialCertificateSection idPrefix="cert-step-fiscalo" />
            <div className="fx-onb-actions" style={{ marginTop: 16 }}>
              <div className="side">
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-secondary"
                  onClick={() => persistFiscaloSubstep(0)}
                >
                  <ArrowLeft aria-hidden />
                  {t("onboarding.certificateStepFiscaloBack")}
                </button>
              </div>
              <div className="side end" />
            </div>
          </>
        }
        brand={<CertificateBrandColumn {...brandColumnProps} />}
      />
    );
  }

  // ── Fallback ─────────────────────────────────────────────────────────
  return (
    <OnboardingShell
      form={
        <>
          <OnboardingBrandMark />
          <h1 className="fx-onb-h1">
            {t("onboarding.step3CertSubtitle", "Certificado")}
          </h1>
        </>
      }
      brand={<CertificateBrandColumn {...brandColumnProps} />}
    />
  );
}
